import { useEffect, useState } from "react";
import { 
  getDisplayKitStats, 
  updateDisplayKitSettings, 
  addDisplayKitStock,
  distributeDisplayKits,
  getDisplayKitHistory 
} from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { 
  Package, 
  Store, 
  AlertTriangle, 
  CheckCircle,
  Clock,
  Plus,
  Settings,
  TrendingDown,
  Truck
} from "lucide-react";
import { toast } from "sonner";

export default function DisplayKits() {
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showAddStock, setShowAddStock] = useState(false);
  const [showDistribute, setShowDistribute] = useState(false);
  
  // Settings form
  const [settingsForm, setSettingsForm] = useState({
    initial_stock: 100,
    kits_per_store: 2,
    lead_time_days: 60,
    reorder_threshold: 20
  });
  
  // Add stock form
  const [addStockQty, setAddStockQty] = useState(50);
  
  // Distribute form
  const [distributeForm, setDistributeForm] = useState({
    store_name: "",
    kits_sent: 2,
    date_sent: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, historyRes] = await Promise.all([
        getDisplayKitStats(),
        getDisplayKitHistory()
      ]);
      setStats(statsRes.data);
      setHistory(historyRes.data);
      if (statsRes.data.settings) {
        setSettingsForm(statsRes.data.settings);
      }
    } catch (error) {
      toast.error("Failed to load display kit data");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSettings = async () => {
    try {
      await updateDisplayKitSettings(settingsForm);
      toast.success("Settings updated successfully");
      setShowSettings(false);
      loadData();
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  const handleAddStock = async () => {
    try {
      await addDisplayKitStock(addStockQty);
      toast.success(`Added ${addStockQty} kits to stock`);
      setShowAddStock(false);
      setAddStockQty(50);
      loadData();
    } catch (error) {
      toast.error("Failed to add stock");
    }
  };

  const handleDistribute = async () => {
    try {
      await distributeDisplayKits(distributeForm);
      toast.success(`Distributed ${distributeForm.kits_sent} kits to ${distributeForm.store_name}`);
      setShowDistribute(false);
      setDistributeForm({ store_name: "", kits_sent: 2, date_sent: new Date().toISOString().split('T')[0] });
      loadData();
    } catch (error) {
      toast.error("Failed to record distribution");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="display-kits-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 font-heading">Display Kits</h2>
          <p className="text-slate-600 mt-1">Track and manage display kit inventory for stores</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowSettings(!showSettings)}
            data-testid="settings-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowAddStock(!showAddStock)}
            data-testid="add-stock-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Stock
          </Button>
          <Button 
            onClick={() => setShowDistribute(!showDistribute)}
            data-testid="distribute-btn"
          >
            <Truck className="w-4 h-4 mr-2" />
            Record Distribution
          </Button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="text-lg">Display Kit Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>Initial Stock</Label>
                <Input 
                  type="number" 
                  value={settingsForm.initial_stock}
                  onChange={(e) => setSettingsForm({...settingsForm, initial_stock: parseInt(e.target.value)})}
                  data-testid="initial-stock-input"
                />
                <p className="text-xs text-slate-500 mt-1">Total kits on day one</p>
              </div>
              <div>
                <Label>Kits Per Store</Label>
                <Input 
                  type="number" 
                  value={settingsForm.kits_per_store}
                  onChange={(e) => setSettingsForm({...settingsForm, kits_per_store: parseInt(e.target.value)})}
                  data-testid="kits-per-store-input"
                />
                <p className="text-xs text-slate-500 mt-1">Kits sent to each new store</p>
              </div>
              <div>
                <Label>Lead Time (Days)</Label>
                <Input 
                  type="number" 
                  value={settingsForm.lead_time_days}
                  onChange={(e) => setSettingsForm({...settingsForm, lead_time_days: parseInt(e.target.value)})}
                  data-testid="lead-time-input"
                />
                <p className="text-xs text-slate-500 mt-1">Reorder lead time</p>
              </div>
              <div>
                <Label>Reorder Threshold</Label>
                <Input 
                  type="number" 
                  value={settingsForm.reorder_threshold}
                  onChange={(e) => setSettingsForm({...settingsForm, reorder_threshold: parseInt(e.target.value)})}
                  data-testid="reorder-threshold-input"
                />
                <p className="text-xs text-slate-500 mt-1">Alert when stock falls below</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
              <Button onClick={handleUpdateSettings} data-testid="save-settings-btn">Save Settings</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Stock Panel */}
      {showAddStock && (
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardHeader>
            <CardTitle className="text-lg">Add New Stock</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-end gap-4">
              <div className="flex-1 max-w-xs">
                <Label>Quantity to Add</Label>
                <Input 
                  type="number" 
                  value={addStockQty}
                  onChange={(e) => setAddStockQty(parseInt(e.target.value))}
                  data-testid="add-stock-qty-input"
                />
              </div>
              <Button variant="outline" onClick={() => setShowAddStock(false)}>Cancel</Button>
              <Button onClick={handleAddStock} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-add-stock-btn">
                Add {addStockQty} Kits
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Distribute Panel */}
      {showDistribute && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader>
            <CardTitle className="text-lg">Record Kit Distribution</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Store Name</Label>
                <Input 
                  value={distributeForm.store_name}
                  onChange={(e) => setDistributeForm({...distributeForm, store_name: e.target.value})}
                  placeholder="Enter store name"
                  data-testid="distribute-store-input"
                />
              </div>
              <div>
                <Label>Kits Sent</Label>
                <Input 
                  type="number" 
                  value={distributeForm.kits_sent}
                  onChange={(e) => setDistributeForm({...distributeForm, kits_sent: parseInt(e.target.value)})}
                  data-testid="distribute-qty-input"
                />
              </div>
              <div>
                <Label>Date Sent</Label>
                <Input 
                  type="date" 
                  value={distributeForm.date_sent}
                  onChange={(e) => setDistributeForm({...distributeForm, date_sent: e.target.value})}
                  data-testid="distribute-date-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDistribute(false)}>Cancel</Button>
              <Button onClick={handleDistribute} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-distribute-btn">
                Record Distribution
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Active Stores</CardTitle>
            <Store className="w-4 h-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900 font-mono" data-testid="active-stores-count">
              {stats?.active_stores || 0}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Stores receiving {stats?.settings?.kits_per_store || 2} kits each
            </p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Kits Distributed</CardTitle>
            <Truck className="w-4 h-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900 font-mono" data-testid="kits-distributed">
              {stats?.kits_distributed || 0}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Out of {stats?.settings?.initial_stock || 0} initial stock
            </p>
          </CardContent>
        </Card>

        <Card className={`card-hover ${stats?.needs_reorder ? 'border-red-300 bg-red-50' : ''}`}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Kits Remaining</CardTitle>
            <Package className={`w-4 h-4 ${stats?.needs_reorder ? 'text-red-500' : 'text-emerald-500'}`} />
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold font-mono ${stats?.needs_reorder ? 'text-red-600' : 'text-slate-900'}`} data-testid="kits-remaining">
              {stats?.kits_remaining || 0}
            </div>
            {stats?.needs_reorder && (
              <Badge variant="destructive" className="mt-1">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Reorder Required
              </Badge>
            )}
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Lead Time</CardTitle>
            <Clock className="w-4 h-4 text-slate-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900 font-mono">
              {stats?.settings?.lead_time_days || 60}
            </div>
            <p className="text-xs text-slate-500 mt-1">Days for reorder</p>
          </CardContent>
        </Card>
      </div>

      {/* Reorder Alert */}
      {stats?.needs_reorder && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <AlertTriangle className="w-8 h-8 text-red-500" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-800">Reorder Alert</h3>
                <p className="text-red-700 text-sm">
                  Stock is below threshold ({stats?.settings?.reorder_threshold} kits). 
                  With a {stats?.settings?.lead_time_days} day lead time, place your order now.
                </p>
              </div>
              <Button 
                variant="outline" 
                className="border-red-300 text-red-700 hover:bg-red-100"
                onClick={() => setShowAddStock(true)}
              >
                Add Stock
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Stores List & Distribution History */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Stores */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading">Active Stores ({stats?.active_stores || 0})</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.active_store_names?.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {stats.active_store_names.map((store, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                    <span className="text-sm font-medium">{store}</span>
                    <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {stats?.settings?.kits_per_store || 2} kits
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm">No active stores yet</p>
            )}
          </CardContent>
        </Card>

        {/* Distribution History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading">Recent Distributions</CardTitle>
          </CardHeader>
          <CardContent>
            {history.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.slice(0, 10).map((dist, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                    <div>
                      <span className="text-sm font-medium">{dist.store_name}</span>
                      <p className="text-xs text-slate-500">{dist.date_sent}</p>
                    </div>
                    <Badge variant="outline">
                      {dist.kits_sent} kits
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm">
                No manual distributions recorded. Auto-calculated from active stores.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
